/**
 * Auth Types for CircleBe SPA Token Management
 * Handles access + refresh token patterns with rotation
 */

/**
 * Represents the authentication session state
 */
export interface AuthSession {
  /** User ID from the token response */
  userId: string;

  /** Access token for API requests (stored in memory) */
  accessToken: string;

  /** When the access token expires */
  expiresAt: Date;

  /** Refresh token for obtaining new access tokens (persisted in localStorage) */
  refreshToken: string;

  /** Optional: when the refresh token expires */
  refreshExpiresAt?: Date;

  /** Email confirmation status from backend */
  emailConfirmed: boolean;

  /** Profile completion status from backend */
  profileConfirmed: boolean;
}

/**
 * Backend token response model (from POST /token or /check-user-status or /refresh)
 * Covers the response from:
 * - POST /api/v1/account/token
 * - POST /api/v1/account/check-user-status
 * - POST /api/v1/account/refresh
 */
export interface TokenResponseModel {
  id: string;
  token?: string; // "token" field from POST /token
  accessToken?: string; // "accessToken" field from POST /refresh (alternative)
  expires?: string; // ISO-8601 or epoch (if string with ms, e.g. "1708XXXXXXXX000")
  expiresAtUtc?: string; // ISO-8601 format
  expiresAtEpoch?: number; // Epoch milliseconds (alternative)
  refreshToken: string;
  refreshExpiresAtUtc?: string; // ISO-8601 format
  refreshExpiresAtEpoch?: number; // Epoch milliseconds

  // From /refresh endpoint response
  emailConfirmed: boolean;
  profileConfirmed: boolean;
  code?: string; // Error code if present
}

/**
 * Refresh request body for POST /api/v1/account/refresh
 */
export interface RefreshTokenRequest {
  refreshToken: string;
  deviceId?: string;
}

/**
 * Custom error thrown when user needs to login
 */
export class NeedsLoginError extends Error {
  constructor(message: string = 'Session expired. Please log in again.') {
    super(message);
    this.name = 'NeedsLoginError';
  }
}

/**
 * Custom error thrown when refresh fails
 */
export class RefreshFailedError extends Error {
  constructor(message: string = 'Failed to refresh session.') {
    super(message);
    this.name = 'RefreshFailedError';
  }
}
